"use client";

import React from "react";
// @ts-ignore
import { ComposableMap, Geographies, Geography } from "react-simple-maps";

import worldMap from "../../public/worldmap.json";

type Country = {
  name: string;
  waves: string;
  color: string;
  url?: string;
  wavesColor?: string;
};

type RegionData = {
  position: [number, number];
  countries: Country[];
};

const studyLocations: Record<string, RegionData> = {
  "North America": {
    position: [-100, 45],
    countries: [
      {
        name: "United States",
        waves: "(+1)",
        color: "#228B22",
        url: `/hcap-studies/overview/?study=HRS-HCAP`,
        wavesColor: "#228B22",
      },
      {
        name: "Mexico",
        waves: "(+1)",
        color: "#228B22",
        url: "/hcap-studies/overview/?study=Mex-Cog",
        wavesColor: "#228B22",
      },
    ],
  },
  Europe: {
    position: [10, 55],
    countries: [
      {
        name: "England",
        waves: "(+1)",
        color: "#228B22",
        url: "/hcap-studies/overview/?study=ELSA-HCAP",
        wavesColor: "#228B22",
      },
      { name: "Northern Ireland", waves: "", color: "#228B22", url: "/hcap-studies/overview/?study=NICOLA-HCAP" },
      { name: "Denmark", waves: "", color: "#228B22", url: "/hcap-studies/overview/?study=SHARE-HCAP&cohort=Denmark" },
      {
        name: "Czech Republic",
        waves: "",
        color: "#228B22",
        url: "/hcap-studies/overview/?study=SHARE-HCAP&cohort=Czech%20Republic",
      },
      { name: "Germany", waves: "", color: "#228B22", url: "/hcap-studies/overview/?study=SHARE-HCAP&cohort=Germany" },
      { name: "Italy", waves: "", color: "#228B22", url: "/hcap-studies/overview/?study=SHARE-HCAP&cohort=Italy" },
      { name: "France", waves: "", color: "#228B22", url: "/hcap-studies/overview/?study=SHARE-HCAP&cohort=France" },
      { name: "Ireland", waves: "", color: "#6072b7", url: "" },
    ],
  },
  Asia: {
    position: [100, 30],
    countries: [
      { name: "China", waves: "", color: "#228B22", url: "/hcap-studies/overview/?study=CHARLS-HCAP" },
      {
        name: "India",
        waves: "(+1)",
        color: "#228B22",
        url: "/hcap-studies/overview/?study=LASI-DAD",
        wavesColor: "#3a7b57",
      },
      { name: "Nepal", waves: "", color: "#e7aa80" },
      { name: "Lebanon", waves: "", color: "#e7aa80" },
    ],
  },
  "Central and South America": {
    position: [-70, -15],
    countries: [
      {
        name: "Chile",
        waves: "",
        color: "#228B22",
        url: "/hcap-studies/overview/?study=Chile-Cog",
      },
      { name: "Caribbean", waves: "", color: "#6072b7", url: "" },
      { name: "Costa Rica", waves: "", color: "#6072b7", url: "" },
      { name: "Brazil (pilot)", waves: "(pilot)", color: "#e7aa80", wavesColor: "#cb5257" },
    ],
  },
  Africa: {
    position: [20, -10],
    countries: [
      {
        name: "South Africa",
        waves: "(+1)",
        color: "#228B22",
        url: "",
        wavesColor: "#228B22",
      },
      { name: "Kenya", waves: "", color: "#e7aa80" },
      { name: "Egypt", waves: "", color: "#e7aa80" },
      { name: "Ghana (pilot)", waves: "(pilot)", color: "#e7aa80", wavesColor: "#cb5257" },
    ],
  },
};

type RegionBoxProps = {
  region: string;
  data: RegionData;
  x: number;
  y: number;
};

function RegionBox({ region, data, x, y }: RegionBoxProps) {
  const boxWidth = 200;
  const boxHeight = data.countries.length * 20 + 25;

  return (
    <g>
      {/* Box background */}
      <rect
        x={x - boxWidth / 2 + 10}
        y={y - boxHeight / 2 + 4}
        width={boxWidth}
        height={boxHeight}
        fill="white"
        stroke="black"
        strokeWidth={2}
        rx={10}
        opacity={0.4}
      />

      {/* Region title */}
      <text x={x - boxWidth / 2 + 15} y={y - boxHeight / 2 + 20} fontSize={14} fontWeight="bold" fill="#68696e">
        {region}:
      </text>

      {/* Countries list */}
      {data.countries.map((country, index) => {
        const textElement = (
          <text
            key={index}
            x={x - boxWidth / 2 + 15}
            y={y - boxHeight / 2 + 40 + index * 20}
            fontSize={12}
            fill={country.color}
            style={{ cursor: country.url ? "pointer" : "default" }}
          >
            • {country.name}
          </text>
        );

        if (country.url && country.url.trim() !== "") {
          return (
            <a key={index} href={country.url} rel="noopener noreferrer" style={{ textDecoration: "none" }}>
              {textElement}
            </a>
          );
        }

        return textElement;
      })}
    </g>
  );
}

const HCAPStudyLocationsDiagram = () => {
  return (
    <div className="container">
      <div className="position-relative overflow-visible mb-3">
        <div className="bg-light rounded">
          <ComposableMap
            projection="geoMercator"
            projectionConfig={{
              scale: 140,
              center: [0, 20],
            }}
            width={1000}
            height={500}
            style={{ width: "100%", height: "auto" }}
          >
            <Geographies geography={worldMap}>
              {({ geographies }: { geographies: Geography[] }) =>
                geographies.map((geo) => (
                  <Geography key={geo.rsmKey} geography={geo} fill="#d1d5db" stroke="#ffffff" strokeWidth={0.5} />
                ))
              }
            </Geographies>

            {/* Region boxes */}
            <RegionBox region="North America" data={studyLocations["North America"]} x={200} y={120} />

            <RegionBox region="Europe" data={studyLocations["Europe"]} x={520} y={100} />

            <RegionBox region="Asia" data={studyLocations["Asia"]} x={750} y={140} />

            <RegionBox
              region="Central and South America"
              data={studyLocations["Central and South America"]}
              x={280}
              y={320}
            />

            <RegionBox region="Africa" data={studyLocations["Africa"]} x={520} y={280} />
          </ComposableMap>
        </div>
      </div>

      {/* Legend */}
      <div className="w-100">
        <div className="bg-white p-3 border border-secondary rounded shadow-sm">
          <div className="small d-flex align-items-center justify-content-evenly flex-wrap gap-3">
            <span style={{ color: "#228B22" }}>Data available</span>
            <span style={{ color: "#6072b7" }}>Data currently being collected or processed</span>
            <span style={{ color: "#e7aa80" }}>Data collection planned</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HCAPStudyLocationsDiagram;
